const SOURCE_LOGIN_MODERN = 'login-modern'
const SOURCE_LOGIN_LEGACY = 'login-legacy'
const SOURCE_PASSWORD = 'password'
const MIN_REQUEST_WINDOW_MS = 30000
const EXPECTED_SETUP_LIFETIME_MS = 600000
const EXPIRY_SKEW_MS = 5000

let current = null

const decodePayload = token => {
  if (!token || typeof token !== 'string') return null
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const padded = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=')
    return JSON.parse(window.atob(padded))
  } catch (error) {
    return null
  }
}

const isSafeEpoch = value => Number.isSafeInteger(value) && value >= 0
const isNonblank = value => typeof value === 'string' && value.length > 0
const isNonce = value => isNonblank(value) && /^[A-Za-z0-9_-]{43}$/.test(value)

const normalizeReturnTo = value => {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return '/'
  return value
}

const modernIdentityFrom = payload => ({
  flowHash: payload.flowHash,
  generation: payload.loginGeneration,
  jti: payload.loginJti,
  clientFence: payload.clientFence,
  ownerNonce: payload.ownerNonce,
})

const validateModern = (handoff, payload) => {
  const identity = handoff.loginIntent && handoff.loginIntent.identity
  if (!identity || !isNonblank(handoff.loginIntent.token)) return false
  const expected = modernIdentityFrom(payload)
  return isNonblank(expected.flowHash)
    && isSafeEpoch(expected.generation)
    && isNonblank(expected.jti)
    && isSafeEpoch(expected.clientFence)
    && isNonce(expected.ownerNonce)
    && Object.keys(expected).every(key => String(identity[key]) === String(expected[key]))
}

const validateLegacy = (handoff, payload) => {
  const lane = handoff.legacyLane
  return lane
    && isNonblank(payload.legacyLaneHash)
    && isSafeEpoch(payload.legacyGeneration)
    && isNonblank(payload.legacyRequestId)
    && String(lane.laneHash) === String(payload.legacyLaneHash)
    && Number(lane.generation) === Number(payload.legacyGeneration)
    && String(lane.requestId) === String(payload.legacyRequestId)
}

const validatePassword = (handoff, payload) => {
  const { credential } = handoff
  return credential
    && isNonblank(payload.sid)
    && isSafeEpoch(payload.ae)
    && isSafeEpoch(payload.se)
    && String(credential.userId) === String(payload.sub)
    && String(credential.sid) === String(payload.sid)
    && Number(credential.ae) === Number(payload.ae)
    && Number(credential.se) === Number(payload.se)
}

export const validatePhoneSetupHandoff = (handoff, options = {}) => {
  const now = options.now == null ? Date.now() : options.now
  const minRemainingMs = options.minRemainingMs == null ? 0 : options.minRemainingMs
  if (!handoff || !isNonblank(handoff.token)) return false
  const payload = decodePayload(handoff.token)
  if (!payload || payload.purpose !== 'phone-setup' || !isNonblank(payload.sub) || !isNonblank(payload.setupJti)) return false
  if (payload.iss !== 'diko-auth' || payload.aud !== 'phone-setup') return false
  // Authority for expiry/lifetime is the signed token's own `iat`/`exp` claims,
  // never a client-receipt-time value: a response delayed by network/event-loop
  // latency must not be rejected merely because it arrived later than expected.
  if (!isSafeEpoch(payload.exp) || !isSafeEpoch(payload.iat)) return false
  const signedExpiry = payload.exp * 1000
  const signedIssuedAt = payload.iat * 1000
  if (Math.abs((signedExpiry - signedIssuedAt) - EXPECTED_SETUP_LIFETIME_MS) > EXPIRY_SKEW_MS) return false
  if (signedExpiry - now < minRemainingMs) return false

  if (handoff.source === SOURCE_LOGIN_MODERN) {
    return payload.origin === 'login'
      && payload.loginCoordinationKind === 'modern'
      && validateModern(handoff, payload)
  }
  if (handoff.source === SOURCE_LOGIN_LEGACY) {
    return payload.origin === 'login'
      && payload.loginCoordinationKind === 'legacy'
      && validateLegacy(handoff, payload)
  }
  if (handoff.source === SOURCE_PASSWORD) {
    return payload.origin === 'password' && validatePassword(handoff, payload)
  }
  return false
}

export function setPhoneSetupHandoff(data) {
  // tokenExpiresAt/issuedAt are derived from the signed token's own exp/iat
  // claims, not trusted from the caller (which historically computed them
  // from response-receipt time and could drift from the signed value under
  // any network/event-loop delay).
  const payload = data && decodePayload(data.token)
  const tokenExpiresAt = payload && isSafeEpoch(payload.exp) ? payload.exp * 1000 : null
  const issuedAt = payload && isSafeEpoch(payload.iat) ? payload.iat * 1000 : Date.now()
  const candidate = data ? {
    token: data.token,
    tokenExpiresAt,
    issuedAt,
    source: data.source,
    returnTo: normalizeReturnTo(data.returnTo),
    loginIntent: data.loginIntent || null,
    legacyLane: data.legacyLane || null,
    credential: data.credential || null,
  } : null
  if (!validatePhoneSetupHandoff(candidate)) {
    current = null
    throw new TypeError('Invalid phone-setup handoff')
  }
  current = candidate
  return current
}

export function getPhoneSetupHandoff() {
  return current
}

export function getValidPhoneSetupHandoff(options = {}) {
  if (!validatePhoneSetupHandoff(current, options)) return null
  return current
}

export function canStartPhoneSetupRequest(handoff = current) {
  return validatePhoneSetupHandoff(handoff, { minRemainingMs: MIN_REQUEST_WINDOW_MS })
}

export function clearPhoneSetupHandoff() {
  current = null
}

export {
  SOURCE_LOGIN_MODERN,
  SOURCE_LOGIN_LEGACY,
  SOURCE_PASSWORD,
  MIN_REQUEST_WINDOW_MS,
}
